import type { ReactElement } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { useGetMyProfileQuery } from '../../../services/ProfileService';
import { hasRevRecAccess } from '../../profile';

/**
 * Route-level authorization guard for the RevRec-claim-gated revenue recognition routes
 * (US2, spec 179, T028).
 *
 * Mounts `children` ONLY once the profile has resolved AND the user has the **RevRec** claim — and,
 * where a route names a `role`, holds exactly that profile role — so an unauthorized user's page
 * shell and data hooks never mount (no partial rendering, no period read firing). The loading state
 * is not a nicety: without it the page flashes the home screen and then redirects, which shows a
 * user a page they are not allowed to see.
 */
export function RequireRevRec(
  { children, role }: { children: ReactElement; role?: string },
): ReactElement | null {
  const { data: profile, isLoading } = useGetMyProfileQuery();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress data-testid="revrec-auth-loading" />
      </Box>
    );
  }

  if (!hasRevRecAccess(profile))
    return <Navigate to="/" replace />;

  // The named role exactly — the plain Administrator does not close or unlock a period.
  if (role && profile?.role !== role)
    return <Navigate to="/" replace />;

  return children;
}
